<template>
	<div class="sdlt-form">
		<header v-if="!$integrationOptions.mode && step === 1" class="mb-2">
			<h1 class="alpha heavy center">Generate your no obligation land tax calculation quote</h1>
		</header>
		<div class="sdlt-form__content">
			<section class="sdlt-form__content__editorial section section--floating">
				<header>
					<h1 class="alpha heavy">About The Compass Land Tax Calculation Service</h1>
				</header>
				<div class="editorial mt-2">
					<p>When buying a new property, one of the key expenses to consider is the Stamp Duty Land Tax (SDLT) as per HMRC regulations. SDLT is a self-assessment tax that varies depending on your specific transaction and personal circumstances.</p>
					<p>Compass Gateway is an accurate Stamp Duty assessment service to ensure you pay the correct amount of tax to HMRC—not too much and not too little. Compass calculates the payable amount by examining transaction details, property information, and personal circumstances, taking into account all applicable reliefs, surcharges, and exemptions. The service includes comprehensive consultancy and advice on your calculation.</p>
					<p>Upon calculation you will receive a Certificate, confirming that Compass has completed your Stamp Duty calculation. Additionally, an audit document will be provided, including a detailed breakdown of the assessment, calculation, and history of your responses. This document is essential for recording your payment with HMRC. As advised by HMRC, please retain your records for at least six years from the effective date of the transaction. Failure to maintain adequate records for the required period may result in a penalty.</p>
					<p>Please complete the questionnaire for us to provide a quote for calculating your Stamp Duty obligation. Once completed, a quote will be generated and emailed to you directly. All quotes are backed by our 'Best Price Guarantee.' (Terms and Conditions apply)</p>
				</div>
			</section>
			<form
				class="section section--floating"
				@submit.prevent="$emit('submit', form)">
				<fieldset>
					<legend><span class="alpha heavy">About The Purchase</span></legend>
					<div class="input-group mt-2">
						<label><span>Type of client </span>
							<select v-model="form.data.data.landTaxCalculation.type_of_client">
								<option disabled selected value="">—</option>
								<option value="individual">Individual</option>
								<option value="couple">Couple</option>
								<option value="joint">Joint</option>
								<option value="partnership">Partnership</option>
								<option value="llp">LLP</option>
								<option value="limited_company">Limited Company</option>
								<option value="charity">Charity</option>
								<option value="sole_member_pension">Sole member pension</option>
								<option value="multiple_member_pension">Multiple member pension</option>
								<option value="sole_member_trust">Sole member trust</option>
								<option value="multiple_member_trust">Multiple member trust</option>
								<option value="housing_association">Housing association</option>
								<option value="local_authority">Local authority</option>
							</select>
							<div v-if="form.errors.get('data.landTaxCalculation.type_of_client')" class="error-block">
								<p v-html="form.errors.get('data.landTaxCalculation.type_of_client')"></p>
							</div>
						</label>
						<label><span>Property price </span>
							<money v-model="form.data.data.landTaxCalculation.property_price"/>
							<div
								v-if="form.errors.get('data.landTaxCalculation.property_price')"
								class="error-block">
								<p v-html="form.errors.get('data.landTaxCalculation.property_price')"></p>
							</div>
						</label>
						<fieldset class="checkbox-group">
							<legend><span class="baseline">Please select all of the following that apply?</span>
							</legend>
							<div>
								<label class="checkbox"><span>You are buying a single home </span>
									<input
										v-model="form.data.data.landTaxCalculation.is_purchasing_single_home"
										type="checkbox">
								</label>
								<label class="checkbox"><span>You already own other property </span>
									<input
										v-model="form.data.data.landTaxCalculation.already_own_other_property"
										type="checkbox">
								</label>
								<label class="checkbox"><span>You are a first time buyer </span>
									<input
										v-model="form.data.data.landTaxCalculation.is_first_time_buyer" type="checkbox">
								</label>
								<label class="checkbox"><span>The purchase will be shared ownership </span>
									<input
										v-model="form.data.data.landTaxCalculation.purchase_is_shared_ownership"
										type="checkbox">
								</label>
								<label class="checkbox"><span>You are buying a part built site </span>
									<input
										v-model="form.data.data.landTaxCalculation.is_purchasing_part_built_site"
										type="checkbox">
								</label>
								<label class="checkbox"><span>The purchase is commercial </span>
									<input
										v-model="form.data.data.landTaxCalculation.is_purchasing_commercial"
										type="checkbox">
								</label>
								<label class="checkbox"><span>The purchase involves a divorce </span>
									<input
										v-model="form.data.data.landTaxCalculation.purchase_involves_divorce"
										type="checkbox">
								</label>
								<label class="checkbox"><span>You are buying multiple properties </span>
									<input
										v-model="form.data.data.landTaxCalculation.is_purchasing_multiple_properties"
										type="checkbox">
								</label>
								<label class="checkbox"><span>The purchase is a subsale </span>
									<input
										v-model="form.data.data.landTaxCalculation.is_purchasing_subsale"
										type="checkbox">
								</label>
								<label class="checkbox"><span>The purchase has connected parties </span>
									<input
										v-model="form.data.data.landTaxCalculation.purchase_has_connected_parties"
										type="checkbox">
								</label>
								<label class="checkbox"><span>The purchase could be uninhabitable </span>
									<input
										v-model="form.data.data.landTaxCalculation.could_be_uninhabitable"
										type="checkbox">
								</label>
								<label class="checkbox"><span>The purchase might be mixed use </span>
									<input
										v-model="form.data.data.landTaxCalculation.potential_mixed_use"
										type="checkbox">
								</label>
								<label class="checkbox"><span>The purchase involves a transfer of equity </span>
									<input
										v-model="form.data.data.landTaxCalculation.transfer_of_equity"
										type="checkbox">
								</label>
								<label class="checkbox"><span>The purchase involves probate </span>
									<input
										v-model="form.data.data.landTaxCalculation.probate"
										type="checkbox">
								</label>
								<label class="checkbox"><span>The purchase isn't described by any of the above </span>
									<input
										v-model="form.data.data.landTaxCalculation.is_purchasing_other" type="checkbox">
								</label>
							</div>
						</fieldset>

						<label><span>Is the property leasehold? </span>
							<select v-model="form.data.data.landTaxCalculation.leasehold">
								<option disabled selected value="">—</option>
								<option :value="false">No</option>
								<option :value="true">Yes</option>
							</select>
							<div v-if="form.errors.get('data.landTaxCalculation.leasehold')" class="error-block">
								<p v-html="form.errors.get('data.landTaxCalculation.leasehold')"></p>
							</div>
						</label>
						<label><span>Are you a UK resident? </span>
							<select v-model="form.data.data.landTaxCalculation.uk_resident">
								<option disabled selected value="">—</option>
								<option :value="false">No</option>
								<option :value="true">Yes</option>
							</select>
							<div v-if="form.errors.get('data.landTaxCalculation.uk_resident')" class="error-block">
								<p v-html="form.errors.get('data.landTaxCalculation.uk_resident')"></p>
							</div>
						</label>
						<label><span>Are there 2 weeks or less until planned completion? </span>
							<select v-model="form.data.data.landTaxCalculation.completing_soon">
								<option :value="false">No</option>
								<option :value="true">Yes</option>
							</select>
							<div v-if="form.errors.get('data.landTaxCalculation.completing_soon')" class="error-block">
								<p v-html="form.errors.get('data.landTaxCalculation.completing_soon')"></p>
							</div>
						</label>
						<label
							v-if="form.data.data.landTaxCalculation.completing_soon"><span>How many days until planned completion? </span>
							<input
								v-model="form.data.data.landTaxCalculation.days_until_completion" step="1"
								type="number">
							<div
								v-if="form.errors.get('data.landTaxCalculation.days_until_completion')"
								class="error-block">
								<p v-html="form.errors.get('data.landTaxCalculation.days_until_completion')"></p>
							</div>
						</label>
					</div>
				</fieldset>
				<ul class="actions actions--right">
					<li>
						<process-button
							:processing="form.processing"
							class="button button--primary--wide" text="Get Your Quote"/>
					</li>
				</ul>
			</form>
		</div>
	</div>
</template>
<script>
import TabbedForm from "../../../common/components/forms/tabbed-form.vue";
import Accordion from "../../../common/components/v-accordion/Accordion.vue";
import AccordionGroup from "../../../common/components/v-accordion/AccordionGroup.vue";
import Address from "../../../components/Address.vue";
import AddressLookup from "../../../components/forms/fields/AddressLookup.vue";
import scrollTo from "../../../mixins/scrollTo";

export default {
	components: {
		Accordion,
		AccordionGroup,
		Address,
		AddressLookup,
		TabbedForm,
	},

	mixins: [
		scrollTo,
	],

	data() {
		return {
			step: null,
			totalSteps: null,
			selectedTab: null,
			tabIsAStep: null,
		};
	},

	computed: {
		tabs() {
			return this.$refs.tabs;
		},
		showSaleTab() {
			return ["sale", "sale_purchase"].indexOf(this.form.data.data.conveyancing.quote_type) > -1;
		},
		showPurchaseTab() {
			return ["purchase", "sale_purchase"].indexOf(this.form.data.data.conveyancing.quote_type) > -1;
		},
		showRemortgageTab() {
			return ["remortgage"].indexOf(this.form.data.data.conveyancing.quote_type) > -1;
		},
		quoteTypeDisplay() {
			return {
				sale_purchase: "Sale & Purchase",
				purchase: "Purchase",
				sale: "Sale",
				remortgage: "Remortgage",
			}[this.form.data.data.conveyancing.quote_type];
		},
	},

	methods: {
		quoteTypeClass(quoteType) {
			return {
				"quote-type--radio--selected": quoteType === this.form.data.data.conveyancing.quote_type,
			};
		},
		lenderName(lenderId) {
			return _.find(this.form.options.lenders, function (lender) {
				return lender.id === lenderId;
			}).name;
		},
		labelClass(field) {
			return {
				disabled: this.isDisabled(field),
			};
		},
		isDisabled(field) {
			return field !== true;
		},
		propertyAddressIsOk(service) {
			let address = this.form.getData("data.conveyancing." + service + ".address");
			return address?.address_country !== "Scotland";
		},
		validate(section, callback) {
			this.form.validate(section).post(this.route("/quotes/services/:service", this.$route.params)).then(response => {
				callback();
			});
		},
	},

	props: {
		form: {
			required: true,
			type: Object,
		},
		processing: {
			required: true,
		},
		error: {
			required: false,
		},
		quoteType: {
			required: false,
		},
	},

	watch: {
		error: {
			handler: function (error) {
				if (!error) {
					this.form.errors.clear();
					return;
				}

				this.refs.tabbedForm.refs.tabs.goToTab("quote-type");
				this.form.fail(error);
			},
		},
		form: {
			deep: true,
			handler(formData, oldFormData) {
				if (formData.data.data.conveyancing.quote_type !== oldFormData.data.data.conveyancing.quote_type) {
					this.form.reset();
				}
			},
		},
	},

	mounted() {
		this.tellParentToReport("quoteFormDisplayed");

		this.form.set("data.conveyancing.quote_type", this.$route.query.quoteType || "");

		if (this.form.data.data.conveyancing.quote_type) {
			this.$nextTick(() => {
				this.refs.tabbedForm.refs.tabs.goToNext();
			});
		}
	},
};
</script>
